import type {
  TravelJoyPlacePricingAndBookingDetailsFragment,
  TravelPlacePricingAndBookingDetailsFragment,
} from '../../apps/guest/packages/layout-engine/widgets/Custom/components/Accommodations/Accommodations.graphql.generated';
import type { TravelCurrencyCode } from '@graphql/generated-base';
import { sanitizeLocale } from '@localization/safeIntl';

/**
 * Rounds up minor units to the nearest whole number for display purposes
 * @param valueInMinorUnits - The price value in minor units (e.g., cents)
 * @param digits - The number of decimal digits (e.g., 2 for cents)
 * @returns The rounded up value in minor units
 * @example roundUpMinorUnits(37983, 2) => 38000 (379.83 -> 380.00)
 */
export const roundUpMinorUnits = (valueInMinorUnits: number, digits: number): number => {
  const divisor = Math.pow(10, digits);
  return Math.ceil(valueInMinorUnits / divisor) * divisor;
};

export const translatedPrice = (priceInMinorUnits: number, digits: number, currency: string, language: Navigator['language'] = 'en-US', showDecimals: boolean = false) => {
  const minorUnits = showDecimals ? priceInMinorUnits : roundUpMinorUnits(priceInMinorUnits, digits);
  // `language` is a raw Navigator locale, which toLocaleString rejects with a
  // RangeError on non-BCP 47 tags like "en-US@posix".
  return (minorUnits / Math.pow(10, digits)).toLocaleString(sanitizeLocale(language), {
    style: 'currency',
    ...(showDecimals ? { maximumFractionDigits: digits, minimumFractionDigits: digits } : { maximumFractionDigits: 0, minimumFractionDigits: 0 }),
    currency,
  });
};

export type CostProperty = Readonly<{
  minorUnits: number;
  display: string;
}>;

/**
 * Converts an amount in minor units to a `CostProperty` object with a formatted display string.
 *
 * @param amount - An object containing:
 *   - valueInMinorUnits: The amount in minor currency units.
 *   - currency: The currency code.
 *   - digits: (Optional) Number of decimal digits for the currency. Defaults to 2 if not provided.
 * @param locale - The locale to use for formatting the currency.
 * @param display - The display format to use. Defaults to 'rounded'.
 * @returns A `CostProperty` object with the minor units and a formatted display string.
 */
export const minorUnitsToCostProperty = (
  amount: Readonly<{ valueInMinorUnits: number; currency: TravelCurrencyCode; digits?: number }>,
  locale: Navigator['language'],
  display: 'rounded' | 'decimal' = 'rounded',
): CostProperty => {
  const digits = amount.digits ?? 2;
  return {
    minorUnits: amount.valueInMinorUnits,
    display: translatedPrice(amount.valueInMinorUnits, digits, amount.currency, locale, display === 'decimal'),
  };
};

export const getPercentageOff = (
  minimumPercentageOffToDisplay: number,
  priceInMinorUnits?: number,
  strikeOutPriceInMinorUnits?: number,
  priceDigits?: number,
  strikeOutPriceDigits?: number,
): number | undefined => {
  if (!priceInMinorUnits || !strikeOutPriceInMinorUnits || !priceDigits || !strikeOutPriceDigits) return undefined;

  const price = priceInMinorUnits / Math.pow(10, priceDigits);
  const strikeOutPrice = strikeOutPriceInMinorUnits / Math.pow(10, strikeOutPriceDigits);
  const percentageOff = Math.round(((strikeOutPrice - price) / strikeOutPrice) * 100);
  return percentageOff >= minimumPercentageOffToDisplay ? percentageOff : undefined;
};

export const getCalculatedPrice = (
  priceDataFromApi: TravelPlacePricingAndBookingDetailsFragment | TravelJoyPlacePricingAndBookingDetailsFragment | null,
  language: Navigator['language'],
  minimumPercentageOffToDisplay: number,
  travelPlaceId?: string | null,
) => {
  // Per-room pricing — fixes multi-room display bug (TRAV-2208)
  const priceData = priceDataFromApi?.pricingInfo?.perNightPerOccupancyBaseAmount;
  const minorUnits = priceData?.valueInMinorUnits;
  const minorUnitsDigits = priceData?.digits;
  const currency = priceData?.currency;
  const price =
    travelPlaceId && minorUnits && currency ? minorUnitsToCostProperty({ valueInMinorUnits: minorUnits, currency, digits: minorUnitsDigits }, language).display : undefined;
  const originalPrice =
    travelPlaceId && minorUnits && currency && minorUnitsDigits
      ? minorUnitsToCostProperty({ valueInMinorUnits: minorUnits, digits: minorUnitsDigits, currency }, language).display
      : undefined;
  const strikeOutPriceData = priceDataFromApi?.pricingInfo?.perNightPerOccupancyStrikeoutBaseAmount;
  const strikeOutMinorUnits = strikeOutPriceData?.valueInMinorUnits;
  const strikeOutMinorUnitsDigits = strikeOutPriceData?.digits;
  const strikeOutCurrency = strikeOutPriceData?.currency;
  const strikeOutPriceValid = strikeOutMinorUnits && minorUnits && strikeOutMinorUnits > minorUnits;

  const strikeOutPrice =
    strikeOutPriceValid && strikeOutMinorUnits && strikeOutCurrency
      ? translatedPrice(roundUpMinorUnits(strikeOutMinorUnits, strikeOutMinorUnitsDigits!), strikeOutMinorUnitsDigits!, strikeOutCurrency, language)
      : undefined;

  const percentageOff = getPercentageOff(minimumPercentageOffToDisplay, minorUnits, strikeOutMinorUnits, minorUnitsDigits, strikeOutMinorUnitsDigits);

  const totalPriceData = priceDataFromApi?.pricingInfo?.totalRate;
  const totalMinorUnits = totalPriceData?.valueInMinorUnits;
  const totalMinorUnitsDigits = totalPriceData?.digits ?? 2;
  const totalCurrency = totalPriceData?.currency;
  const totalAmount =
    (travelPlaceId &&
      (totalMinorUnits && totalCurrency ? translatedPrice(roundUpMinorUnits(totalMinorUnits, totalMinorUnitsDigits), totalMinorUnitsDigits, totalCurrency, language) : null)) ||
    undefined;

  return { price, strikeOutPrice, percentageOff, minorUnits, totalAmount, originalPrice };
};
